import React from "react";
import moment from "moment";
import "moment/locale/vi"; // Import Vietnamese locale
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import {
  DateCalendarProps,
  DateView,
  DayCalendarSkeleton,
  LocalizationProvider,
  PickerValidDate,
} from "@mui/x-date-pickers";

import { Stack } from "@mui/material";
import { dayOfWeekMap } from "./const";
import ServerDay, { HighlightDay } from "./ServerDay";
import { DateCalendarCustom } from "./style";
import CustomCalendarHeader from "./CustomCalendarHeader/CustomCalendarHeader";

moment.locale("vi"); // Set the locale to Vietnamese

interface Props extends DateCalendarProps<PickerValidDate> {
  isDisabledWeekend?: boolean;
  hideWeekend?: boolean;
  views?: DateView[];
  headerCustom?: any;
  highlightedDays?: { [key: string]: HighlightDay[] };
  renderDayContent?: (
    initDay: HighlightDay | undefined,
    day: any
  ) => React.ReactNode;
  isDisableDetail?: boolean;
  className?: string;
}

const Calendar: React.FC<Props> = ({
  highlightedDays,
  views = ["year", "day"],
  renderDayContent,
  ...props
}) => {
  const locale = "vi";

  if (moment.locale() !== locale) {
    moment.locale(locale);
  }

  const shouldDisableDate = (
    date: moment.Moment,
    view: DateView,
    currentMonth?: moment.Moment
  ) => {
    // Disable weekends if isDisabledWeekend is true
    if (props.isDisabledWeekend) {
      if (date.day() === 0 || date.day() === 6) {
        return true;
      }
    }

    // Disable days outside the current month
    if (
      currentMonth &&
      date.month() !== currentMonth.month() &&
      props.isDisableDetail
    ) {
      return true;
    }

    return false;
  };

  const hideWeekend = props.hideWeekend;

  const renderOnlyWeekdays = (dayProps: any) => {
    const date = moment(dayProps.day);
    if ((date.day() === 0 || date.day() === 6) && hideWeekend) {
      return null; // Do not render weekends
    }
    const modifiedDayProps = { ...dayProps };

    // If the day is outside the current month, override its mouse and click events
    if (dayProps.outsideCurrentMonth) {
      modifiedDayProps.onClick = (event: any) => {
        event.preventDefault();
        event.stopPropagation();
      };
      modifiedDayProps.onMouseDown = (event: any) => {
        event.preventDefault();
        event.stopPropagation();
      };
    }
    return (
      <ServerDay
        {...modifiedDayProps}
        isDisableDetail={props?.isDisableDetail}
      />
    );
  };

  return (
    <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale={locale}>
      <Stack sx={{ background: "#fff" }}>
        <DateCalendarCustom
          renderLoading={() => <DayCalendarSkeleton />}
          views={views}
          slots={{
            day: (dayProps) => renderOnlyWeekdays(dayProps),
            calendarHeader: props?.headerCustom
              ? (prop) => props.headerCustom(prop) as any
              : CustomCalendarHeader,
          }}
          showDaysOutsideCurrentMonth
          shouldDisableDate={
            (date) =>
              shouldDisableDate(
                moment(date),
                "day",
                moment(props.value || new Date())
              ) // Pass current month
          }
          hideWeekend={hideWeekend}
          fixedWeekNumber={5}
          slotProps={{
            day: {
              highlightedDays,
            } as any,
          }}
          dayOfWeekFormatter={(weekday) => {
            const weekdayIndex = moment(weekday).day();
            if (hideWeekend) {
              // Show only Monday to Friday when hideWeekend is true
              return weekdayIndex >= 1 && weekdayIndex <= 5
                ? dayOfWeekMap[weekdayIndex]
                : ""; // Return an empty string for weekends
            }
            // Show all days when hideWeekend is false
            return dayOfWeekMap[weekdayIndex];
          }}
          {...props}
          className={props.className ?? "calendar-time-keeping"}
        />
      </Stack>
    </LocalizationProvider>
  );
};

export default Calendar;
